import React, { useEffect } from 'react';
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { ToastMessage, useToastStore } from '@/stores/toastStore';

const toneStyles: Record<ToastMessage['tone'], string> = {
  success: 'border-emerald-300 bg-emerald-50 text-emerald-950 dark:border-emerald-700 dark:bg-emerald-950 dark:text-emerald-50',
  error: 'border-red-300 bg-red-50 text-red-950 dark:border-red-700 dark:bg-red-950 dark:text-red-50',
  warning: 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-50',
  info: 'border-blue-300 bg-blue-50 text-blue-950 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-50'
};

const icons = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: TriangleAlert,
  info: Info
};

const ToastItem: React.FC<{ message: ToastMessage }> = ({ message }) => {
  const dismiss = useToastStore((state) => state.dismiss);
  const Icon = icons[message.tone];

  useEffect(() => {
    if (message.duration <= 0) return;
    const timer = window.setTimeout(() => dismiss(message.id), message.duration);
    return () => window.clearTimeout(timer);
  }, [dismiss, message.duration, message.id]);

  return (
    <div
      role={message.tone === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto flex w-full items-start gap-3 rounded-xl border px-4 py-3 shadow-lg ${toneStyles[message.tone]}`}
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1 whitespace-pre-line text-sm font-medium leading-5">{message.message}</p>
      <button
        type="button"
        onClick={() => dismiss(message.id)}
        className="rounded p-0.5 opacity-60 transition hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export const ToastViewport: React.FC = () => {
  const messages = useToastStore((state) => state.messages);

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed right-4 top-4 z-[10000] flex w-[min(24rem,calc(100vw-2rem))] flex-col gap-2"
    >
      {messages.map((message) => <ToastItem key={message.id} message={message} />)}
    </div>
  );
};
